/* ------------------------------------------------------------------
   MusicLibraryModal – Jamendo search & browse (with sort + trending)
------------------------------------------------------------------- */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Modal, Button, Form, InputGroup, Spinner,
  ListGroup, Badge, Dropdown,
} from 'react-bootstrap'

const TAGS  = ['lofi','ambient','hip hop','rock','electronic',
               'acoustic','chill','funk','cinematic','corporate']

/* ── ✅  corrected Jamendo sort values ────────────────────────────── */
const SORTS = [
  { id:'pop_desc',  label:'Most popular',   order:'popularity_total_desc' },
  { id:'list_week', label:'Trending week',  order:'listens_week_desc'     },
  { id:'newest',    label:'Newest',         order:'releasedate_desc'      },
  { id:'alpha',     label:'A → Z',          order:'name_asc'              },
]

export default function MusicLibraryModal ({ show, onHide, onSelect }) {
  const [q, setQ]             = useState('')
  const [order, setOrder]     = useState(SORTS[1].order)    // weekly trending default
  const [tracks, setTracks]   = useState([])
  const [loading, setLoading] = useState(false)
  const [playingId, setPlayingId] = useState(null)
  const [audioEl] = useState(() => new Audio())

  /* pause preview on unmount */
  useEffect(() => () => audioEl.pause(), [audioEl])

  /* fetch helper */
  const fetchTracks = async (query, ord = order) => {
    setLoading(true)
    setTracks([])
    try {
      const res  = await fetch(
        `/api/music/jamendo?q=${encodeURIComponent(query)}&order=${ord}`
      )
      const data = await res.json()
      setTracks(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  /* initial trending list whenever modal opens */
  useEffect(() => {
    if (show) fetchTracks('', 'listens_week_desc')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  /* handlers */
  const handleSearch = e => { e.preventDefault(); fetchTracks(q.trim()) }
  const handleTag    = tag => { setQ(tag); fetchTracks(tag) }
  const handleSort   = s   => { setOrder(s.order); fetchTracks(q, s.order) }

  const handlePreview = t => {
    if (playingId === t.id) { audioEl.pause(); setPlayingId(null); return }
    audioEl.pause()
    audioEl.src = t.mp3
    audioEl.volume = 1
    audioEl.play().catch(() => {})
    setPlayingId(t.id)
  }

  /* UI */
  return (
    <Modal show={show} onHide={onHide} size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Select Background Music</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* search bar + sort */}
        <Form onSubmit={handleSearch}>
          <InputGroup>
            <Form.Control
              placeholder='Search genre, mood, artist…'
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <Dropdown onSelect={id => handleSort(SORTS.find(s => s.id === id))}>
              <Dropdown.Toggle variant='outline-secondary' id='sort-dd'>
                Sort
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {SORTS.map(s => (
                  <Dropdown.Item
                    key={s.id}
                    eventKey={s.id}
                    active={s.order === order}
                  >
                    {s.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button type='submit' variant='primary'>Search</Button>
          </InputGroup>
        </Form>

        {/* quick tags */}
        <div className='mt-3 mb-2'>
          {TAGS.map(tag => (
            <Badge
              key={tag}
              bg='secondary'
              pill
              className='me-2 mb-1'
              style={{ cursor:'pointer' }}
              onClick={() => handleTag(tag)}
            >
              {tag}
            </Badge>
          ))}
        </div>

        {loading && <Spinner animation='border' className='mt-4' />}
        {!loading && tracks.length === 0 &&
          <p className='text-muted mt-4'>
            No results found – try another search or click a tag.
          </p>
        }

        <ListGroup variant='flush' className='mt-2'>
          {tracks.map(t => (
            <ListGroup.Item key={t.id} className='d-flex align-items-center'>
              <img src={t.thumb} alt='' width={48} height={48} className='me-3 rounded' />
              <div className='flex-grow-1'>
                <div>{t.title} <small className='text-muted'>– {t.artist}</small></div>
                <small className='text-muted'>
                  {Math.round(t.duration)} s • {t.license}
                </small>
              </div>

              <Button
                variant='outline-secondary'
                size='sm'
                className='me-2'
                onClick={() => handlePreview(t)}
              >
                {playingId === t.id ? 'Pause' : 'Preview'}
              </Button>

              <Button
                variant='primary'
                size='sm'
                onClick={() => { onSelect(t); audioEl.pause(); onHide() }}
              >
                Use
              </Button>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
    </Modal>
  )
}

MusicLibraryModal.propTypes = {
  show:     PropTypes.bool.isRequired,
  onHide:   PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
}
